import { useEffect } from 'react';
import { getElectronStores } from '../api/stores/electronStores';
import { authState } from '../api/stores/shared';
import { serviceContainer } from '../services/ServiceContainer';

/**
 * Custom hook to send "App Launched" analytics event
 *
 * Waits for both machineId (from IPC replication) and hostAuthId (from Clerk auth)
 * before sending, so the event always carries full identity for Amplitude/BigQuery.
 *
 * In guest mode (window.playcast.guestAccount) hostAuthId will be anonymous and is
 * rejected by the analytics identity system, so we skip the auth gate and send as
 * soon as machineId is available.
 *
 * The analytics identity system (initializeAnalyticsIdentity) registers its listeners
 * in an earlier useEffect in App.tsx, so by the time our listeners fire the identity
 * store is already populated with whatever fields are available.
 *
 * Deduplicates events using sessionStorage (survives page reloads but not app restart).
 *
 * Electron-only hook - does nothing in web context.
 */
export const useAppLaunchedAnalytics = () => {
  useEffect(() => {
    if (!window.playcast?.isHost) return;

    const electronStores = getElectronStores();
    const isGuestMode = !!window.playcast?.guestAccount;
    let hasAttemptedSend = false;

    const checkAndSend = () => {
      if (hasAttemptedSend) return;

      const machineId = electronStores.machineIdState.currentState;
      if (!machineId) return;

      if (!isGuestMode && !authState.currentState.hostAuthId) return;

      if (sessionStorage.getItem('hasSentAppLaunched')) {
        console.log('[App Launched] Already sent for this session');
        return;
      }

      hasAttemptedSend = true;
      serviceContainer.analyticsManager.sendAppLaunchedEvent();

      sessionStorage.setItem('hasSentAppLaunched', 'true');
      console.log('[App Launched] Event sent successfully');
    };

    const machineIdListener = electronStores.machineIdState.addListener((id) => {
      if (id && !hasAttemptedSend) {
        checkAndSend();
      }
    });

    let authListener: string | undefined;
    if (!isGuestMode) {
      authListener = authState.addListener((state) => {
        if (state.hostAuthId && !hasAttemptedSend) {
          checkAndSend();
        }
      });
    }

    checkAndSend();

    return () => {
      electronStores.machineIdState.removeListener(machineIdListener);
      if (authListener) {
        authState.removeListener(authListener);
      }
    };
  }, []);
};
